import { Controller } from "@hotwired/stimulus"
require("select2/dist/css/select2.min.css");
import select2 from "select2/dist/js/select2.full.js";
import jquery from "jquery";

const select2SelectedPreviewSelector = ".select2-selection--single"
const select2SearchInputFieldSelector = ".select2-search__field"

export default class extends Controller {
  static targets = [ "select" ]
  static values = {
    acceptsNew: Boolean,
    enableSearch: Boolean,
    dropdownParentSelector: String,
    searchUrl: String,
    searchPlaceholder: String,
    select2Options: String,
    openOnConnect: Boolean,
  }

  // will be reissued as native dom events name prepended with '$' e.g. '$change', '$select2:closing', etc
  static jQueryEventsToReissue = [ "change", "select2:closing", "select2:close", "select2:opening", "select2:open", "select2:selecting", "select2:select", "select2:unselecting", "select2:unselect", "select2:clearing", "select2:clear" ]


  initialize() {
    this.dispatchNativeEvent = this.dispatchNativeEvent.bind(this)
    this.clearSearchAfterSelect = this.clearSearchAfterSelect.bind(this);
    this.handleOpen = this.handleOpen.bind(this)
    this.formatState = this.formatState.bind(this)
    this.ensurePluginInstance = this.ensurePluginInstance.bind(this)
    this.stopChoiceRemoveClickPropagation = this.stopChoiceRemoveClickPropagation.bind(this)
    this.openRequested = false
    this.ignoreClickUntil = 0
    if (window.jQuery === undefined) {
      window.jQuery = jquery
    }
    if (!this.isSelect2LoadedOnWindowJquery) {
      select2()
    }
  }

  get isSelect2LoadedOnWindowJquery() {
    return window?.jQuery?.fn?.select2 !== undefined
  }

  get optionsOverride() {
    if (!this.hasSelect2OptionsValue) { return {} }
    return this.select2OptionsValue
  }

  // Helper method to safely parse custom options
  getCustomOptions() {
    if (!this.hasSelect2OptionsValue) { return null; }
    try {
      return JSON.parse(this.select2OptionsValue);
    } catch (e) {
      console.warn("Error parsing select2Options:", e);
      return null;
    }
  }

  // Check if closeOnSelect is explicitly set to false
  get isCloseOnSelectFalse() {
    const options = this.getCustomOptions();
    return options && options.closeOnSelect === false;
  }

  connect() {
    this.element.addEventListener('$select2:open', this.handleOpen)
    document.addEventListener("turbo:render", this.ensurePluginInstance)
    document.addEventListener("turbo:frame-load", this.ensurePluginInstance)

    // First ensure any orphaned Select2 instances are cleaned up
    this.cleanupBeforeInit();

    this.ensurePluginInstance()

    // Only attach the clearSearchAfterSelect listener if we're in "stay open" mode
    if (this.isCloseOnSelectFalse) {
      this.element.addEventListener('$select2:select', this.clearSearchAfterSelect);
    }

    if (this.openOnConnectValue) {
      setTimeout(() => {
        this.openAndFocus()
      }, 0)
    }
  }

  disconnect() {
    this.element.removeEventListener('$select2:open', this.handleOpen)
    document.removeEventListener("turbo:render", this.ensurePluginInstance)
    document.removeEventListener("turbo:frame-load", this.ensurePluginInstance)

    // Always try to remove the listener, even if we're not sure it was attached
    // This is safe even if it wasn't attached
    this.element.removeEventListener('$select2:select', this.clearSearchAfterSelect);

    // Try to manually close Select2 if it's open before destroying it
    try {
      if (this.hasActiveSelect2Instance()) {
        jQuery(this.pluginMainEl).select2('close');
      }
    } catch(e) {
      console.warn("Error closing Select2 during disconnect:", e);
    }

    if (this.isSelect2LoadedOnWindowJquery) {
      this.teardownPluginInstance()
    }

    // Extra safety: forcibly remove any remaining Select2 containers
    if (this.element) {
      this.element.querySelectorAll('.select2-container').forEach(el => el.remove());
    }
  }

  cleanupBeforeInit() {
    this.element.querySelectorAll('.select2-container--default').forEach(el => el.remove());
  }

  ensurePluginInstance() {
    if (!this.isSelect2LoadedOnWindowJquery) {
      return
    }

    const currentSelect = this.selectTarget
    if (!currentSelect?.isConnected) {
      return
    }

    const currentInstance = currentSelect.dataset.select2Id ? jQuery(currentSelect).data('select2') : null
    const missingContainer = this.element.querySelector('.select2-container') === null
    const stalePluginMainEl = this.pluginMainEl && this.pluginMainEl !== currentSelect

    if (!currentInstance || missingContainer || stalePluginMainEl) {
      this.teardownPluginInstance()
      this.pluginMainEl = currentSelect
      this.initPluginInstance()
    }
  }

  initPluginInstance() {
    let options = {
      dropdownParent: this.dropdownParentElement
    };

    if (!this.enableSearchValue) {
      options.minimumResultsForSearch = -1;
    }

    if (this.acceptsNewValue) {
      options.tags = true;
      options.createTag = (params) => {
        const term = jQuery.trim(params.term);
        if (term === '') {
          return null;
        }
        return {
          id: term,
          text: term,
          newTag: true
        };
      };
    }

    if (this.searchUrlValue) {
      options.ajax = {
        url: this.searchUrlValue,
        dataType: 'json',
        data: function(params) {
          var query = {
            search: params.term,
            page: params.page || 1
          }
          return query
        }
      }
    }

    options.templateResult = this.formatState;
    options.templateSelection = this.formatSelection.bind(this);
    options.width = 'style';

    // Conditionally set dropdownCssClass using a function if it's a timeline select
    if (this.selectTarget.classList.contains('timeline-super-select')) {
      options.dropdownCssClass = function(element) {
        let classes = 'timeline-dropdown-style';
        return classes;
      };
    }

    const custom_options_string = this.hasSelect2OptionsValue ? this.select2OptionsValue : '{}';
    const custom_options = JSON.parse(custom_options_string);

    if (custom_options.dropdownParentSelector) {
      const customDropdownParent = document.querySelector(custom_options.dropdownParentSelector)
      if (customDropdownParent) {
        options.dropdownParent = jQuery(customDropdownParent)
      }
      delete custom_options.dropdownParentSelector
    }

    // Merge custom options, being careful if custom_options also defines dropdownCssClass
    if (custom_options.dropdownCssClass) {
      if (typeof options.dropdownCssClass === 'function') {
        // If our conditional one is a function, and custom is a string from data attributes,
        // we can't easily merge them directly here without knowing Select2's internal order.
        // For safety, let the custom_options override if it provides a string.
        // Or, one could try to call the function and append the string.
        // For now, let's log a warning and let custom override, or you can decide a merge strategy.
        console.warn("SuperSelect: dropdownCssClass defined both conditionally (as function) and via data-attributes. Data-attribute version will likely take precedence or behavior might be unexpected.");
        options = {...options, ...custom_options};
      } else if (typeof custom_options.dropdownCssClass === 'string' && typeof options.dropdownCssClass === 'string') {
        // Both are strings, combine them
        options.dropdownCssClass = options.dropdownCssClass + ' ' + custom_options.dropdownCssClass;
        options = {...options, ...custom_options, dropdownCssClass: options.dropdownCssClass};
      } else {
        // Our conditional one wasn't set or custom_options.dropdownCssClass is not a string; just merge.
        options = {...options, ...custom_options};
      }
    } else {
      // No custom_options.dropdownCssClass, so just merge normally.
      options = {...options, ...custom_options};
    }

    this.cleanupBeforeInit();
    this.pluginMainEl = this.selectTarget;
    jQuery(this.pluginMainEl).select2(options);
    this.preventChoiceRemoveFromTogglingDropdown();
    this.flushQueuedOpen();

    this.initReissuePluginEventsAsNativeEvents();
  }

  teardownPluginInstance() {
    if (this.pluginMainEl === undefined) { return }

    // ensure there are no orphaned event handlers
    this.allowChoiceRemoveToPropagate()
    this.teardownPluginEventsAsNativeEvents()

    try {
      // Try to close it first (extra safety)
      if (this.hasActiveSelect2Instance()) {
        jQuery(this.pluginMainEl).select2('close');
      }
    } catch(e) {
      console.warn("Error closing Select2 before destroy:", e);
    }

    try {
      // revert to original markup, remove any event listeners
      if (this.hasActiveSelect2Instance()) {
        jQuery(this.pluginMainEl).select2('destroy');
      }
    } catch(e) {
      console.warn("Error destroying Select2:", e);
      // Fallback cleanup if destroy fails
      if (this.element) {
        this.element.querySelectorAll('.select2-container').forEach(el => el.remove());
      }
    }

    // Clear any global document/window event handlers that might have been added
    try {
      jQuery(document).off('.select2');
    } catch(e) {
      console.warn("Error removing Select2 document listeners:", e);
    }
  }

  hasActiveSelect2Instance() {
    return this.pluginMainEl && jQuery(this.pluginMainEl).data('select2')
  }

  get dropdownParentElement() {
    if (this.hasDropdownParentSelectorValue) {
      const element = document.querySelector(this.dropdownParentSelectorValue)
      if (element) return jQuery(element)
    }

    return jQuery(this.element)
  }

  open() {
    jQuery(this.pluginMainEl).select2('open')
  }

  openAndFocus() {
    if (!this.hasActiveSelect2Instance()) {
      this.openRequested = true
      return
    }

    this.open()
    this.focusOnTextField()
  }

  openFromInteraction(event) {
    if (event?.type === "click" && Date.now() < this.ignoreClickUntil) {
      return
    }

    if (this.shouldIgnoreOpenInteraction(event)) {
      return
    }

    if (event?.type === "touchstart") {
      this.ignoreClickUntil = Date.now() + 500
      event.preventDefault()
    }

    this.openAndFocus()
  }

  handleOpen() {
    this.focusOnTextField()
  }

  focusOnTextField(event) {
    this.focusSearchField()
    this.stabilizeSearchFieldFocus()
  }

  stabilizeSearchFieldFocus() {
    const deadline = window.performance.now() + 500
    const focusWhileSettling = () => {
      if (window.performance.now() >= deadline || !this.focusSearchField()) return

      window.requestAnimationFrame(focusWhileSettling)
    }

    window.requestAnimationFrame(focusWhileSettling)
  }

  focusSearchField() {
    const searchField = this.element.querySelector(select2SearchInputFieldSelector)
    if (!searchField?.isConnected || !searchField.closest(".select2-container--open")) return false

    if (this.hasSearchPlaceholderValue) {
      searchField.setAttribute("placeholder", this.searchPlaceholderValue)
      searchField.setAttribute("aria-label", this.searchPlaceholderValue)
    }

    this.guardSearchFieldFocus(searchField)
    searchField.focus()
    return true
  }

  guardSearchFieldFocus(searchField) {
    if (this.focusGuardedSearchField === searchField) return

    this.focusGuardedSearchField = searchField
    searchField.addEventListener("blur", () => {
      window.requestAnimationFrame(() => {
        if (searchField.isConnected && searchField.closest(".select2-container--open")) {
          this.focusSearchField()
        }
      })
    })
  }

  injectKeystrokeIntoTextField(event) {
    if (!event?.srcElement.matches(select2SelectedPreviewSelector)) { return }

    if (["Shift", "Alt", "Control", "Meta", "Tab", "Backspace", "Escape"].includes(event.key)) { return }

    this.open()

    const searchInputField = this.element.querySelector(select2SearchInputFieldSelector)

    if (!searchInputField) { return }

    if (event.type !== "keydown") {
      // since keydown precedes keyup, and since keyup is what sends the key to an input field, this next line isn't necessary if keydown is the event captured. We'll just focus on the input field, and the keyup will caught by the input field naturally.
      searchInputField.value = searchInputField.value + event.key
    }
    searchInputField.focus()
  }

  initReissuePluginEventsAsNativeEvents() {
    this.constructor.jQueryEventsToReissue.forEach((eventName) => {
      jQuery(this.pluginMainEl).on(eventName, this.dispatchNativeEvent)
    })
  }

  teardownPluginEventsAsNativeEvents() {
    this.constructor.jQueryEventsToReissue.forEach((eventName) => {
      jQuery(this.pluginMainEl).off(eventName, this.dispatchNativeEvent)
    })
  }

  preventChoiceRemoveFromTogglingDropdown() {
    const instance = jQuery(this.pluginMainEl).data('select2')
    instance?.$selection?.on(
      'click.superSelectChoiceRemove',
      '.select2-selection__choice__remove',
      this.stopChoiceRemoveClickPropagation
    )
  }

  allowChoiceRemoveToPropagate() {
    const instance = this.pluginMainEl ? jQuery(this.pluginMainEl).data('select2') : null
    instance?.$selection?.off(
      'click.superSelectChoiceRemove',
      '.select2-selection__choice__remove',
      this.stopChoiceRemoveClickPropagation
    )
  }

  stopChoiceRemoveClickPropagation(event) {
    event.stopPropagation()
  }

  dispatchNativeEvent(event) {
    const nativeEventName = '$' + event.type // e.g. '$change.select2'
    this.element.dispatchEvent(new CustomEvent(nativeEventName, { detail: { event: event }, bubbles: true, cancelable: false }))
  }

  clearSearchAfterSelect(event) {
    // Double-check we're still in closeOnSelect false mode before proceeding
    if (!this.isCloseOnSelectFalse) {
      return; // Exit if we're not in "stay open" mode
    }

    const searchInput = this.element.querySelector(select2SearchInputFieldSelector);
    if (searchInput) {
      if (searchInput.value === "") {
        return;
      }

      searchInput.value = "";
      // Trigger an input event to make Select2 update its displayed results
      // based on the now-empty search field.
      searchInput.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
    }
  }

  flushQueuedOpen() {
    if (!this.openRequested) {
      return
    }

    this.openRequested = false
    this.openAndFocus()
  }

  shouldIgnoreOpenInteraction(event) {
    const target = event?.target
    if (!(target instanceof Element)) {
      return false
    }

    if (target.matches(select2SearchInputFieldSelector)) {
      return true
    }

    return target.closest(".select2-container--open .select2-dropdown, .select2-selection__choice__remove, .select2-selection__clear") !== null
  }

  // https://stackoverflow.com/questions/29290389/select2-add-image-icon-to-option-dynamically
  formatState(opt) {
    const sanitizedText = sanitizeHTML(opt.text || "");
    if (this.isNewTagOption(opt)) {
      return jQuery('<span><span class="new-tag-sparkle">✨</span> Create ' + sanitizedText + '</span>');
    }

    const preview = this.formatPreviewState(opt)
    if (preview) {
      return preview
    }

    const resultLabel = opt.element?.dataset?.select2ResultLabel
    if (resultLabel) {
      const result = document.createElement("span")
      result.textContent = resultLabel
      return jQuery(result)
    }

    var imageUrl = opt.element?.dataset.image
    var imageHtml = "";
    if (imageUrl) {
      imageHtml = '<img src="' + imageUrl + '" /> ';
    }
    return jQuery('<span>' + imageHtml + sanitizedText + '</span>');
  }

  formatPreviewState(opt) {
    const data = opt.element?.dataset
    if (!data?.select2PreviewPersonName) {
      return null
    }

    const preview = document.createElement("div")
    preview.className = "inline-tail min-w-0 py-0.5 leading-4"
    preview.dataset.controller = "inline-tail"
    preview.dataset.inlineTailPolicyValue = "block"
    preview.dataset.select2OptionLabel = data.select2PreviewPersonName
    if (data.select2PreviewTitle) {
      preview.title = data.select2PreviewTitle
    }

    const lead = this.appendTextElement(preview, "span", "font-medium text-slate-900 dark:text-white", data.select2PreviewPersonName)
    lead.dataset.inlineTailRole = "lead"
    this.appendPreviewContextElement(preview, data)
    this.appendPreviewPlanningContextElement(preview, data)
    this.appendPreviewOccupancyElement(preview, data)
    this.appendPreviewSlotCommentElement(preview, data)

    return jQuery(preview)
  }

  appendPreviewContextElement(parent, data) {
    if (!data.select2PreviewContext) {
      return null
    }

    const hasConflictIcon = data.select2PreviewStatus && data.select2PreviewStatus !== "assigned"
    return this.appendPreviewUnit(parent, {
      kind: "context",
      text: data.select2PreviewContext,
      className: "text-xs leading-4 text-slate-500 dark:text-slate-300",
      iconClassName: hasConflictIcon ? "fa-light fa-object-intersect text-amber-500 dark:text-amber-300" : null,
      ariaLabel: hasConflictIcon && data.select2PreviewIconLabel
        ? `${data.select2PreviewIconLabel}: ${data.select2PreviewContext}`
        : null
    })
  }

  appendPreviewSlotCommentElement(parent, data) {
    if (!data.select2PreviewSlotComment) {
      return null
    }

    return this.appendPreviewUnit(parent, {
      kind: "slot-comment",
      text: data.select2PreviewSlotComment,
      className: "text-xs leading-4 text-slate-400 dark:text-slate-400"
    })
  }

  appendPreviewPlanningContextElement(parent, data) {
    if (!data.select2PreviewPlanningContext) {
      return null
    }

    const presentation = this.planningEffectPresentation(data.select2PreviewPlanningEffect)
    return this.appendPreviewIcon(parent, {
      kind: "planning-context",
      label: data.select2PreviewPlanningContext,
      title: data.select2PreviewPlanningContextTitle || data.select2PreviewPlanningContext,
      iconClassName: presentation.iconClassName,
      ariaLabel: data.select2PreviewPlanningEffectLabel
        ? `${data.select2PreviewPlanningEffectLabel}: ${data.select2PreviewPlanningContext}`
        : null
    })
  }

  appendPreviewOccupancyElement(parent, data) {
    if (!data.select2PreviewOccupancy) {
      return null
    }

    return this.appendPreviewIcon(parent, {
      kind: "occupancy",
      label: data.select2PreviewOccupancy,
      title: data.select2PreviewOccupancy,
      iconClassName: "fa-light fa-clock text-amber-500 dark:text-amber-300",
      ariaLabel: data.select2PreviewOccupancy
    })
  }

  appendPreviewIcon(parent, {kind, label, title, iconClassName, ariaLabel = null}) {
    const lead = parent.querySelector('[data-inline-tail-role="lead"]')
    const target = lead || parent
    const unit = document.createElement("span")
    unit.className = "select2-preview-icon ml-0.5 inline-flex align-middle"
    unit.dataset.select2PreviewUnit = kind
    unit.dataset.select2PreviewLabel = label
    unit.title = title
    if (ariaLabel) unit.setAttribute("aria-label", ariaLabel)

    const icon = document.createElement("i")
    icon.className = iconClassName
    icon.setAttribute("aria-hidden", "true")
    unit.appendChild(icon)
    if (lead) lead.appendChild(document.createTextNode("\u2060"))
    target.appendChild(unit)
    if (lead) parent.classList.add("select2-preview-has-lead-icon")
    return unit
  }

  appendPreviewUnit(parent, {kind, text, className, iconClassName = null, ariaLabel = null}) {
    const unit = document.createElement("span")
    unit.className = className
    unit.dataset.inlineTailRole = "unit"
    unit.dataset.select2PreviewUnit = kind
    if (ariaLabel) {
      unit.setAttribute("aria-label", ariaLabel)
    }

    const separator = this.appendTextElement(unit, "span", "leading-none", "·")
    separator.dataset.inlineTailRole = "separator"
    separator.setAttribute("aria-hidden", "true")

    const content = document.createElement("span")
    content.className = "min-w-0 break-words whitespace-normal"
    content.dataset.inlineTailRole = "content"

    if (iconClassName) {
      const icon = document.createElement("i")
      icon.className = `mr-1 inline-block ${iconClassName}`
      icon.setAttribute("aria-hidden", "true")
      content.appendChild(icon)
    }
    content.appendChild(document.createTextNode(text))

    unit.appendChild(content)
    parent.appendChild(unit)
    return unit
  }

  appendTextElement(parent, tagName, className, text) {
    if (!text) {
      return null
    }

    const element = document.createElement(tagName)
    element.className = className
    element.textContent = text
    parent.appendChild(element)
    return element
  }

  formatSelection(data, container) {
    // Add class to the <li> container if it's a new tag and container is valid
    if (this.isNewTagOption(data) && container) {
      jQuery(container).addClass('select2-selection__choice--new');
    }

    const previewPersonName = data.element?.dataset?.select2PreviewPersonName
    if (previewPersonName) {
      const selection = document.createElement("span")
      const previewData = data.element?.dataset || {}
      const presentation = this.selectionPreviewPresentation(previewData)

      if (presentation) {
        if (presentation.warning) {
          jQuery(container).addClass("select2-selection__choice--conflict-preview")
        }
        selection.className = "inline-flex items-center gap-1"
        if (presentation.label) {
          selection.setAttribute("aria-label", `${presentation.label}: ${previewPersonName}`)
        }

        const icon = document.createElement("i")
        icon.className = `${presentation.iconClassName} shrink-0`
        icon.setAttribute("aria-hidden", "true")
        selection.appendChild(icon)

        this.appendTextElement(selection, "span", "", previewPersonName)
      } else {
        selection.textContent = previewPersonName
      }

      return jQuery(selection)
    }

    const selectionLabel = data.element?.dataset?.select2SelectionLabel
    if (selectionLabel) {
      const selection = document.createElement("span")
      selection.textContent = selectionLabel
      return jQuery(selection)
    }

    const sanitizedText = sanitizeHTML(data.text || ""); // Sanitize ONLY the user-provided text

    if (this.shouldRenderCreateSelectionLabel(data)) {
      // Return a jQuery object: icon in its own span + sanitized text
      return jQuery('<span><span class="new-tag-sparkle">✨</span> Create ' + sanitizedText + '</span>');
    } else if (this.isNewTagOption(data)) {
      return jQuery('<span><span class="new-tag-sparkle">✨</span> ' + sanitizedText + '</span>');
    } else {
      // Return a jQuery object: just sanitized text
      return jQuery('<span>' + sanitizedText + '</span>');
    }
  }

  selectionPreviewPresentation(data) {
    if (data.select2PreviewPlanningEffect === "negative") {
      return {
        iconClassName: "fa-light fa-thumbs-down text-rose-600 dark:text-rose-300",
        label: data.select2PreviewPlanningEffectLabel,
        warning: true
      }
    }

    if (data.select2PreviewStatus && !["assigned", "planning"].includes(data.select2PreviewStatus)) {
      return {
        iconClassName: "fa-light fa-object-intersect text-amber-600 dark:text-amber-300",
        label: data.select2PreviewIconLabel,
        warning: true
      }
    }

    if (data.select2PreviewOccupancy) {
      return {
        iconClassName: "fa-light fa-clock text-amber-600 dark:text-amber-300",
        label: data.select2PreviewOccupancy,
        warning: true
      }
    }

    if (data.select2PreviewPlanningEffect) {
      const presentation = this.planningEffectPresentation(data.select2PreviewPlanningEffect)
      return {
        iconClassName: presentation.iconClassName,
        label: data.select2PreviewPlanningEffectLabel,
        warning: false
      }
    }

    return null
  }

  planningEffectPresentation(effect) {
    switch (effect) {
      case "positive":
        return {
          iconClassName: "fa-light fa-thumbs-up text-emerald-600 dark:text-emerald-300"
        }
      case "negative":
        return {
          iconClassName: "fa-light fa-thumbs-down text-rose-600 dark:text-rose-300"
        }
      default:
        return {
          iconClassName: "fa-light fa-circle-info text-sky-600 dark:text-sky-300"
        }
    }
  }

  isNewTagOption(option) {
    return option?.newTag === true || option?.element?.dataset?.newTag === "true"
  }

  shouldRenderCreateSelectionLabel(option) {
    return option?.element?.dataset?.createSelectionLabel === "true"
  }
}

// https://portswigger.net/web-security/cross-site-scripting/preventing
function sanitizeHTML(str) {
  return str.replace(/[^\w. ]/gi, function (c) {
    return '&#' + c.charCodeAt(0) + ';';
  });
};
